import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../../api/client';
import { fillHtml } from '../../lib/richText';

/** What a message template's placeholders are filled with. */
export interface TemplateVars {
  name?: string | null;
  shop?: string | null;
  shopId?: string | null;
}

/** The tenant page's set (also used for activation codes) or a support inbox conversation's. */
export type TemplateKind = 'tenant' | 'inbox';

/** Placeholders the admin can use in Settings → Message templates. */
export const TEMPLATE_PLACEHOLDERS: { key: keyof TemplateVars; label: string }[] = [
  { key: 'name', label: 'owner’s name' },
  { key: 'shop', label: 'shop name' },
  { key: 'shopId', label: 'Shop ID' },
];

/** Fills {name}, {shop}, {shopId} in a plain-text template; one without a value is left out. */
export function fillTemplate(template: string, vars: TemplateVars): string {
  return template
    .replace(/\{(name|shop|shopId)\}/g, (_, k: keyof TemplateVars) => vars[k] ?? '')
    .replace(/[ \t]+([,.])/g, '$1')
    .replace(/\b(Hello|Dear|Hi|Salam)\s*,/g, '$1,')
    .trim();
}

/** The same for an HTML email body (values are escaped). */
export function fillEmailTemplate(template: string, vars: TemplateVars): string {
  return fillHtml(template, { name: vars.name, shop: vars.shop, shopId: vars.shopId }).replace(
    /\b(Hello|Dear|Hi|Salam)\s*,/g,
    '$1,',
  );
}

/** The templates kept in platform settings (shared cache with the Settings page). */
export function useTemplates() {
  const settings = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => unwrap(api.GET('/platform/settings')),
  });
  const s = settings.data;
  return (kind: TemplateKind) =>
    s
      ? kind === 'inbox'
        ? {
            whatsapp: s.inboxWhatsappTemplate,
            subject: s.inboxEmailSubject,
            body: s.inboxEmailBody,
          }
        : {
            whatsapp: s.tenantWhatsappTemplate,
            subject: s.tenantEmailSubject,
            body: s.tenantEmailBody,
          }
      : null;
}
